import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { useCustomerTypeRedirector } from '@/hooks/useCustomerTypeRedirector';
import { usePersistIsExternal } from '@/hooks/usePersistIsExternal';
import useCustomer from '@/hooks/user/useCustomer';
import { PATIENT_ORDERS_ROUTE } from '@/routes/route-paths';
import { Logger } from '@/utils/logger';
import useUser from '@montugroup/circuit-shared/hooks/user/useUser';
import AuthService from '@montugroup/circuit-shared/services/authentication.service';
import { isPatientPortalPartnerPage } from './utils';

import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import Error404 from '@/pages/error/404';
import { ACTOR_TYPE, useSessionHeartbeat } from '@montugroup/circuit-shared';
import { CircularProgressWrapper } from '@montugroup/circuit-shared/components/common/CircularProgressWrapper';
import { PageLayout } from '../layout/PageLayout';
import { PortalPartnerPageLayout } from '../layout/portal-partner-layout/PortalPartnerPageLayout';

const logger = new Logger('requireAuth');

function RequireAuth() {
  const { data: user, isLoading: userIsLoading } = useUser();
  const { data: customer, isLoading: customerIsLoading } = useCustomer();
  const enableEscriptOrders = useFeatureFlag('enable_escript_orders');
  const shouldUseSessionHeartbeat = useFeatureFlag('auth_enable_session_heartbeat');
  const location = useLocation();
  const { isPendingRedirection } = useCustomerTypeRedirector();
  useSessionHeartbeat({ enabled: Boolean(user && shouldUseSessionHeartbeat) });
  usePersistIsExternal();

  const isEscriptCustomer = customer?.isExternal && enableEscriptOrders;

  useEffect(() => {
    if (!user && !userIsLoading) {
      logger.info('useEffect logout');
      AuthService.logout({ reason: 'expired' });
    }
  }, [location.pathname, user, userIsLoading]);

  // NOTE: !customer should be handled in the App.tsx global loading spinner but due to some network failure edge cases this also needs to include that check
  if (!user || !customer || isPendingRedirection) {
    logger.warn('loadingSpinnerHit', {
      userIsLoading,
      '!user': !user,
      '!customer': !customer,
      customerIsLoading,
      isPendingRedirection,
    });

    return <CircularProgressWrapper />;
  }

  logger.info('notLoading');

  // Check if user is patient actorType - required for all customer-portal routes
  if (user.actorType !== ACTOR_TYPE.patient) {
    logger.info('User is not patient actorType', {
      pathname: location.pathname,
      'user.id': user.id,
      'user.actorType': user.actorType,
    });

    return <Error404 />;
  }

  const isPortalPartnerPage = isPatientPortalPartnerPage(location.pathname);
  const isPortalPartnerLayoutForOrdersHistory = location.pathname === PATIENT_ORDERS_ROUTE;

  return (
    <div>
      {isPortalPartnerPage || isPortalPartnerLayoutForOrdersHistory || !isEscriptCustomer ? (
        <PortalPartnerPageLayout>
          <Outlet />
        </PortalPartnerPageLayout>
      ) : (
        <PageLayout>
          <Outlet />
        </PageLayout>
      )}
    </div>
  );
}

export default RequireAuth;
